import React, { useState } from 'react';
import FocusTrap from 'focus-trap-react';
import {
  Box,
  Text,
  Overlay,
  OverlayCenter,
  OverlayBackdrop,
  IconButton,
  Icon,
  Icons,
  config,
} from 'folds';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { stopPropagation } from '../../utils/keyboard';
import settings from '../../../client/state/settings';

type SettingsModalProps = {
  open: boolean;
  onClose: () => void;
};

const LANGUAGE_OPTIONS = [
  { code: 'zh', label: '繁體中文' },
  { code: 'en', label: 'English' },
];

function isDarkModeActive(): boolean {
  return document.body.classList.contains('dark-theme');
}

function ToggleSwitch({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      style={{
        position: 'relative',
        width: '44px',
        height: '24px',
        borderRadius: '12px',
        border: 'none',
        background: checked ? 'var(--curve-primary, #22C55E)' : 'var(--curve-border, #CBD5E1)',
        cursor: 'pointer',
        transition: 'background 0.2s',
        flexShrink: 0,
        outline: 'none',
        padding: 0,
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: '3px',
          left: checked ? '23px' : '3px',
          width: '18px',
          height: '18px',
          borderRadius: '50%',
          background: 'var(--curve-toggle-dot, #FFFFFF)',
          boxShadow: 'var(--curve-toggle-dot-shadow)',
          transition: 'left 0.2s',
        }}
      />
    </button>
  );
}

export function SettingsModal({ open, onClose }: SettingsModalProps) {
  const { t } = useTranslation();
  const currentLang = i18n.language?.split('-')[0] ?? 'zh';
  const [isDark, setIsDark] = useState<boolean>(isDarkModeActive);

  const handleLangChange = (code: string) => {
    i18n.changeLanguage(code);
  };

  const handleDarkModeToggle = (value: boolean) => {
    setIsDark(value);
    settings.useSystemTheme = false;
    settings.setTheme(value ? 2 : 0);
  };

  return (
    <Overlay open={open} backdrop={<OverlayBackdrop />}>
      <OverlayCenter>
        <FocusTrap
          focusTrapOptions={{
            initialFocus: false,
            clickOutsideDeactivates: true,
            onDeactivate: onClose,
            escapeDeactivates: stopPropagation,
          }}
        >
          <div
            style={{
              background: 'var(--curve-bg-white, #FFFFFF)',
              color: 'var(--curve-text-dark)',
              borderRadius: '12px',
              boxShadow: 'var(--curve-shadow-modal, 0 8px 32px rgba(0,0,0,0.18))',
              width: '360px',
              maxWidth: '90vw',
              overflow: 'hidden',
            }}
          >
            {/* 標題列 */}
            <Box
              alignItems="Center"
              justifyContent="SpaceBetween"
              style={{
                padding: `${config.space.S400} ${config.space.S400} ${config.space.S300}`,
                borderBottom: '1px solid var(--curve-border, #E5E5E5)',
              }}
            >
              <Text size="H4">{t('Settings.title')}</Text>
              <IconButton onClick={onClose} size="300" radii="300">
                <Icon size="200" src={Icons.Cross} />
              </IconButton>
            </Box>

            {/* 內容 */}
            <Box
              direction="Column"
              gap="400"
              style={{ padding: config.space.S400 }}
            >
              {/* 日夜模式 */}
              <Box direction="Column" gap="200">
                <Text size="L400" priority="300">
                  {t('Settings.appearance')}
                </Text>
                <Box
                  alignItems="Center"
                  justifyContent="SpaceBetween"
                  style={{
                    padding: `${config.space.S200} ${config.space.S300}`,
                    borderRadius: '8px',
                    border: '1.5px solid var(--curve-border, #E5E5E5)',
                  }}
                >
                  <Box alignItems="Center" gap="200">
                    <Icon
                      size="200"
                      src={isDark ? Icons.Bulb : Icons.Sun}
                      style={{ color: isDark ? 'var(--curve-icon-bulb, #94A3B8)' : 'var(--curve-icon-sun, #F59E0B)' }}
                    />
                    <Text size="T300">
                      {isDark ? t('Settings.dark_mode') : t('Settings.light_mode')}
                    </Text>
                  </Box>
                  <ToggleSwitch checked={isDark} onChange={handleDarkModeToggle} />
                </Box>
              </Box>

              {/* 語言設定 */}
              <Box direction="Column" gap="200">
                <Text size="L400" priority="300">
                  {t('Settings.language')}
                </Text>
                <Box direction="Column" gap="100">
                  {LANGUAGE_OPTIONS.map((opt) => {
                    const isSelected = currentLang === opt.code;
                    return (
                      <button
                        key={opt.code}
                        type="button"
                        onClick={() => handleLangChange(opt.code)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          width: '100%',
                          padding: `${config.space.S200} ${config.space.S300}`,
                          borderRadius: '8px',
                          border: `1.5px solid ${isSelected ? 'var(--curve-primary, #22C55E)' : 'var(--curve-border, #E5E5E5)'}`,
                          background: isSelected ? 'var(--curve-primary-soft, rgba(34,197,94,0.08))' : 'transparent',
                          cursor: 'pointer',
                          transition: 'border-color 0.15s, background 0.15s',
                          outline: 'none',
                        }}
                      >
                        <Text
                          size="T300"
                          style={{
                            color: isSelected ? 'var(--curve-primary, #22C55E)' : 'inherit',
                            fontWeight: isSelected ? 600 : 400,
                          }}
                        >
                          {opt.label}
                        </Text>
                        {isSelected && (
                          <Icon
                            size="200"
                            src={Icons.Check}
                            style={{ color: 'var(--curve-primary, #22C55E)' }}
                          />
                        )}
                      </button>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          </div>
        </FocusTrap>
      </OverlayCenter>
    </Overlay>
  );
}
